import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import { emptyVideo, Video } from "../inspection/Video";
import Language from "../translation/language";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { CodeDetection, ConfidenceLevels, DetectionsAtTime, emptyVideoAnalysisDataResponse, GroupCodesWithMainCodes, PredictionDetails, PredictionGroup, TimestampPrediction, VideoAnalysisDataResponse } from "../../api-models/response/VideoAnalysisDataResponse";
import { FileStates } from "../shop/ShopHistoryStorage";
import { translationService } from "../translation/TranslationService";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";
import { endpoints } from "../api/ApiEndpoints";
import serializeToQueryParams from "../../helpers/QueryParams";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { sortHexBasedOnLuminance } from "../../helpers/ColorHelpers";
import { api } from "../api/ApiService";
import { requestVaDataOnce } from "../api/VaDataRequestCache";
import { uploadFilesService } from "../upload/UploadFilesService";
import { ActivityTypes } from "../shop/ActivityPricesStorage";
import { toJS } from "mobx";
import { SecondaryCodeEnum } from "../../generated/Damages";
import { ServiceModel } from "../app/ServiceStorage";
import { AppStorageModel } from "../app/AppStorage";

export const AnalysisServiceModel = types.compose(
    ServiceModel,
    types.model({
        language: types.enumeration<Language>(Object.values(Language)),
        analysisContent: types.optional(types.maybeNull(types.string), null),
        detectedMainCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
        detectedCharacterizations: types.optional(types.array(types.frozen<CodeDetection>()), []),
        isOverviewDisplayed: true,
        codesAvailableForDetection: types.optional(
            types.array(types.frozen<GroupCodesWithMainCodes>()),
            []
        ),
        vaData: types.optional(
            types.frozen<VideoAnalysisDataResponse>(),
            emptyVideoAnalysisDataResponse
        ),
        plotNeedsRelayout: false,
        analysisConfidenceOption: ConfidenceOptions.Recommended,
        firstTimeChoosingAnalysisIndividualConfidence: true,
        shouldShowAnalysisIndivididualConfidenceModal: false,
        analysisIndividualConfidenceLevels: types.optional(types.frozen<ConfidenceLevels>(), {}),
        analysisOverallConfidence: types.optional(types.number, 0.75),
    })
).views((self) => ({
    get mainCodeColors(): any {
        return self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat()
          .reduce((accumulator, value) => {
            return { ...accumulator, [value.mainCode]: value.color };
          }, {});
    },
    get allPredictions() {
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
  
        let combinedPredictions: TimestampPrediction[] = [];
  
        for (const mainCode of flatted) {
          var confidence = 0;
          if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
            confidence = self.vaData.recommendedConfidenceLevels[mainCode.mainCode]
          } else {
            if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
              if (self.analysisIndividualConfidenceLevels?.[mainCode.mainCode]) {
                confidence = self.analysisIndividualConfidenceLevels?.[mainCode.mainCode];
              }
            } else {
              confidence = self.analysisOverallConfidence
            }
          }
          
          if (confidence > 1) confidence = confidence / 100;
          // Guard: this main-code/confidence bucket may be absent (e.g. the forced 100%
          // default, or a code with no predictions at that level). Object.values(undefined)
          // throws and crashes the analysis/comparison tabs — skip instead. Accumulate with
          // push (O(n)) rather than repeated spread (O(n²)).
          const object = self.vaData?.predictionsPerAccuracy?.[mainCode.mainCode + "_" + confidence];
          if (object) {
            for (const prediction of Object.values(object) as TimestampPrediction[]) {
              combinedPredictions.push(prediction);
            }
          }
        }
  
        return combinedPredictions;
    },
    // Predictions bucketed by their rounded timestamp, built once from allPredictions.
    // characterizationAtTime() used to .filter(allPredictions) on EVERY call, and it's
    // called once per RoI inside the graph/comparison useMemos — O(RoIs x predictions),
    // which froze large files. This map turns each lookup into O(1). Cached by MST while
    // observed (i.e. during observer renders), and invalidated whenever allPredictions is.
    get predictionsByRoundedTime(): Map<number, TimestampPrediction[]> {
        const map = new Map<number, TimestampPrediction[]>();
        const preds = (self as any).allPredictions as TimestampPrediction[];
        for (const p of preds) {
          const key = Math.round(p.timestamp);
          const bucket = map.get(key);
          if (bucket) bucket.push(p);
          else map.set(key, [p]);
        }
        return map;
    },
    get sectionLength(): number {
        return self.vaData?.sectionLength ?? 0;
    },
    // Detections placed along the pipe: each prediction mapped to its metre
    // position via the time->metre Distances table, plus its main-code colour.
    get pipeMarkers(): { timestamp: number; mainCode: string; meters: number; color: string }[] {
        const colors = (self as any).mainCodeColors as Record<string, string>;
        const preds = (self as any).allPredictions as TimestampPrediction[];
        const distances = self.vaData?.distances ?? [];

        const distanceAt = (time: number): number => {
          let meters = 0;
          let bestTime = -Infinity;
          for (const d of distances) {
            if (d.time <= time && d.time > bestTime) {
              bestTime = d.time;
              meters = d.meters;
            }
          }
          return meters;
        };

        return preds
          .filter((p) => p?.predictionDetails?.mainCode)
          .map((p) => ({
            timestamp: p.timestamp,
            mainCode: p.predictionDetails.mainCode,
            meters: distanceAt(p.timestamp),
            color: colors[p.predictionDetails.mainCode] ?? "gray",
          }));
    },
    // True when the analysis JSON provided RoIs (detection regions). Region-level
    // detections carry their own peak distance, so they're preferred over the
    // per-timestamp predictions when available.
    get hasRoiData(): boolean {
        return Object.keys(self.vaData?.roisPerAccuracy ?? {}).length > 0;
    },
    // Region-level detections at the currently-selected confidence (per main code),
    // each with its peak time + distance + accuracy. Mirrors the confidence
    // selection used by allPredictions.
    get roiDetections(): { mainCode: string; time: number; meters: number; beginDistance: number; endDistance: number; accuracy: number; color: string }[] {
        const colors = (self as any).mainCodeColors as Record<string, string>;
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
        const out: { mainCode: string; time: number; meters: number; beginDistance: number; endDistance: number; accuracy: number; color: string }[] = [];
        for (const mc of flatted) {
          let confidence = 0;
          if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
            confidence = self.vaData.recommendedConfidenceLevels[mc.mainCode];
          } else if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
            if (self.analysisIndividualConfidenceLevels?.[mc.mainCode]) {
              confidence = self.analysisIndividualConfidenceLevels[mc.mainCode];
            }
          } else {
            confidence = self.analysisOverallConfidence;
          }
          if (confidence > 1) confidence = confidence / 100;
          const regions = self.vaData.roisPerAccuracy?.[mc.mainCode + "_" + confidence];
          if (!regions) continue;
          for (const r of regions) {
            out.push({
              mainCode: r.mainCode,
              time: r.time,
              meters: r.distance,
              beginDistance: r.beginDistance,
              endDistance: r.endDistance,
              accuracy: r.accuracy,
              color: colors[r.mainCode] ?? "gray",
            });
          }
        }
        return out;
    },
    // Region-level detections at the LOWEST confidence bucket (0.65) for every main
    // code — the full superset regardless of the active confidence selector. Used
    // by the comparison to surface a ground-truth code's AI detection even when the
    // current threshold would hide it, so matches always appear.
    get roiDetectionsAtMinConfidence(): { mainCode: string; time: number; meters: number; beginDistance: number; endDistance: number; accuracy: number; color: string }[] {
        const colors = (self as any).mainCodeColors as Record<string, string>;
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
        const out: { mainCode: string; time: number; meters: number; beginDistance: number; endDistance: number; accuracy: number; color: string }[] = [];
        for (const mc of flatted) {
          const regions = self.vaData.roisPerAccuracy?.[mc.mainCode + "_0.65"];
          if (!regions) continue;
          for (const r of regions) {
            out.push({
              mainCode: r.mainCode,
              time: r.time,
              meters: r.distance,
              beginDistance: r.beginDistance,
              endDistance: r.endDistance,
              accuracy: r.accuracy,
              color: colors[r.mainCode] ?? "gray",
            });
          }
        }
        return out;
    },
    get predictionsForSmallestAccuracy() {
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
  
        let combinedPredictions: TimestampPrediction[] = [];
  
        for (const mainCode of flatted) {
          // Guard the 0.65 bucket (may be missing → Object.values(undefined) throws) and
          // accumulate with push (O(n)) instead of repeated spread (O(n²)).
          const bucket = self.vaData?.predictionsPerAccuracy?.[`${mainCode.mainCode}_${"0.65"}`];
          if (!bucket) continue;
          for (const prediction of Object.values(bucket) as TimestampPrediction[]) {
            combinedPredictions.push(prediction);
          }
        }
  
        return combinedPredictions;
      },
      get analysisNotStarted(): boolean {
        return self.status === FileStates.None;
      },
      get analysisFailed(): boolean {
        return self.status === FileStates.Failed;
      },
      get analysisRunning(): boolean {
        return self.status === FileStates.Running;
      },
      get analysisSuccessful(): boolean {
        return self.status === FileStates.Success;
      },
  
      get pauses() {
        const pauses = self.vaData?.pauses ?? [];
        return (self.vaData?.detectedTimestamps ?? []).map((x) => {
          const timestampIntervalCheck = pauses.find((p) => p.begin <= x && p.end >= x);

          if (timestampIntervalCheck) return 1;
          return 0;
        });
      },

      get allTimestamps() {
        return self.vaData?.detectedTimestamps ?? [];
      },
}))
.actions((self) => ({
    getSortedCodesByColor: (codes: GroupCodesWithMainCodes[]): GroupCodesWithMainCodes[] => {
        const sortedCodes: GroupCodesWithMainCodes[] = [];

        // The response can omit codesAvailableForDetection (null) even when the
        // file is flagged successful — guard so we don't crash on null.forEach.
        (codes ?? []).forEach((code) => {
          sortedCodes.push({
            groupCode: code.groupCode,
            color: code.color,
            mainCodesWithSecondaryCodes: (code.mainCodesWithSecondaryCodes ?? []).sort((code1, code2) =>
              sortHexBasedOnLuminance(code1.color, code2.color)
            ),
          });
        });

        return sortedCodes;
    },

    isMainCodeDetected: (mainCode: string): boolean => {
        return self.predictionsForSmallestAccuracy.some(
          (x) => x.predictionDetails?.mainCode === mainCode
        );
    }
})).extend(self => ({
    actions: {
        clear() {
            self.fileId = undefined;
            self.video = undefined;
            self.status = FileStates.None
            self.startedAt = undefined;
            self.finishedAt = undefined;
        },
        request: flow(function*() {
            const languageQueryParam = translationService.language === Language.english ? 0 : 1;
              if (self.alwaysUseVaData || featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform)) {
                  // Capture the file this request is for. On fast navigation (A -> B) the page
                  // clears this service and reloads for B while A's (slow, multi-second) va-data
                  // is still in flight; when A's response finally lands it must NOT be applied to
                  // B, or the PREVIOUS file's Video Analysis / Comparison tabs keep showing until
                  // a full page refresh.
                  const requestedFileId = self.fileId;
                  const endpointVAData = endpoints.get("video_analysis_data");
                  if (endpointVAData) {
                      endpointVAData.routeParams = [self.fileId!];
                      // Postgres is the source of truth: do NOT send loadIfPresent. The backend
                      // then loads analysis only when the DB says it succeeded
                      // (IsAnalyzed && VideoAutoDetectionWasSuccessful) — a JSON sitting in
                      // storage no longer overrides the DB flag.
                      endpointVAData.queryParams = "";
                  }

                  var responseVAData:any = {};
                  try {
                      // Coalesced with the segmentation service's identical va-data GET for
                      // the same file so the payload is fetched/parsed once, not twice.
                      responseVAData = yield requestVaDataOnce(getEnv(self).api, endpointVAData, self.fileId!);
                  } catch (error) {
                      // Dropped if we've navigated to another file since this started.
                      if (self.fileId !== requestedFileId) return;
                      // va-data failed (e.g. unauthenticated / file without analysis):
                      // keep the fallback video set above and stop, so the line below
                      // never builds a Video from an empty response (would throw on .id).
                      self.video = new Video({...emptyVideo, isConverted: true, analysisSuccessfullyDone: false, segmentationSuccessfullyDone: false});
                      return;
                  }

                  // Stale-response guard: if the loaded file changed while this va-data was in
                  // flight, discard it so it can't resurrect the previous file's analysis state.
                  if (self.fileId !== requestedFileId) return;

                  self.video = new Video(responseVAData.video);
                  self.status = self.getFileState(
                      responseVAData.videoAutoDetectionWasSuccessful,
                      responseVAData.videoAutoDetectionErrorMessage,
                      responseVAData.videoAutoDetectionIsInProgress);
                  self.setTiming(
                      responseVAData.videoAutoDetectionStartedAt,
                      responseVAData.videoAutoDetectionFinishedAt);
                  // Analysis presence is driven purely by the Postgres flag
                  // (video_auto_detection_was_successful, via getFileState above). No storage-
                  // presence override: if the DB says analysis didn't succeed, stop here so the
                  // Video Analysis / Comparison tabs don't show.
                  if (self.status !== FileStates.Success) return;
                  self.codesAvailableForDetection = cast(
                      self.getSortedCodesByColor(responseVAData.codesAvailableForDetection ?? [])
                  );
                  self.vaData = responseVAData;
                  self.analysisOverallConfidence =
                      self.vaData.recommendedConfidenceLevels?.["Video_Analysis"] ??
                      self.analysisOverallConfidence;

                  // Compute the set of detected main codes ONCE. isMainCodeDetected()
                  // recomputes predictionsForSmallestAccuracy on every call; calling it per
                  // code below (and again per characterization) was O(codes² × predictions)
                  // and froze the page while a large analysis file loaded.
                  const detectedMainCodeSet = new Set<string>();
                  for (const prediction of self.predictionsForSmallestAccuracy) {
                      const detectedCode = prediction.predictionDetails?.mainCode;
                      if (detectedCode) detectedMainCodeSet.add(detectedCode);
                  }

                  const mainCodes: CodeDetection[] = [];
                  self.codesAvailableForDetection.forEach((groupCode) => {
                      groupCode.mainCodesWithSecondaryCodes.forEach((mainCode) => {
                          mainCodes.push({
                              code: mainCode.mainCode,
                              colorCode: mainCode.color,
                              isVisible: true,
                              secondaryCodes: mainCode.secondaryCodes,
                              isDetected: detectedMainCodeSet.has(mainCode.mainCode),
                              groupCode: groupCode.groupCode,
                          });
                      });
                  });

                  self.detectedMainCodes = cast(mainCodes);

                  const characterizationCodes: CodeDetection[] = [];
                  for (let key in self.vaData.characterizationCodesWithColors) {
                          characterizationCodes.push({
                          code: key,
                          colorCode: self.vaData.characterizationCodesWithColors[key],
                          isVisible: true,
                          isDetected: detectedMainCodeSet.has(key),
                      });
                  }
                  self.detectedCharacterizations = cast(characterizationCodes);
              } else {
                  const endpoint = endpoints.get("media_file_video_analysis");
                  if (endpoint) {
                      endpoint.routeParams = [self.fileId!];
                      endpoint.queryParams = serializeToQueryParams({ language: languageQueryParam });
                  }
                  const response = yield getEnv(self).api.get(endpoint);
                  self.status = self.getFileState(
                      responseVAData.videoAutoDetectionWasSuccessful, 
                      responseVAData.videoAutoDetectionErrorMessage,
                      responseVAData.videoAutoDetectionIsInProgress);
                  self.analysisContent = response.analysisContent;
              }
        }),
        runService: flow(function* () {
            try {
                const endpoint = endpoints.get("media_file_video_analysis");
                if (endpoint) {
                    endpoint.routeParams = [self.fileId!];
                }
                self.status = FileStates.Running;
                yield getEnv(self).api.put(endpoint);
            } catch (error) {
                self.status = FileStates.None;
                const applicationError = error as ApplicationError;
                getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
                throw applicationError;
            }
        }),
        runAfterUpload: flow(function* () {
          yield self.startService();
          yield self.load();
        })
    }
})).actions((self) => {
    /**
     * One document download for the analysis side.
     *
     * Lives here rather than on the OCR service because these are video-analysis exports: the
     * OCR service owns the OCR ones, and a reader looking for "where does the VA CSV come from"
     * should find it next to the rest of the analysis state.
     *
     * `errorKey` is shown when the file is not there. A 404 is an ordinary answer for all three
     * -- the analyzer may have produced no report, or the review may not be approved yet -- so it
     * is reported as a sentence the reviewer can act on rather than a raw request failure.
     */
    const downloadDocument = flow(function* (
      endpointName: string, fileName: string, errorKey: string
    ) {
        try {
          const endpoint = endpoints.get(endpointName);
          if (endpoint) {
            endpoint.routeParams = [self.fileId!];
          }
          const response = yield getEnv(self).api.download(endpoint);
          const url = URL.createObjectURL(response.data);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = fileName;
          anchor.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          snackbarNotificationsService.showError(errorKey);
        }
    });

    const exportVideoAnalysis = flow(function* () {
        try {
          self.setLoading(true);
          const endpoint = endpoints.get("media_file_analysis_export");
          if (endpoint) {
            endpoint.routeParams = [self.fileId!];
          }
          const response = yield getEnv(self).api.download(endpoint);
          self.setLoading(false);
          return response.data;
        } catch (error) {
          self.setLoading(false);
          const applicationError = error as ApplicationError;
          getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
          throw applicationError;
        }
    });

    return {
        async exportVideoAnalysis(){
          return await exportVideoAnalysis();
        },
        /** The analyzer's own report, exactly as the pipeline wrote it. */
        async downloadAnalysisHtml() {
          await downloadDocument(
            "media_file_video_analysis_export_html",
            "analysis.html",
            "comparison.export.analysis-html.error"
          );
        },
        /** The approved observations as a report of their own. */
        async downloadApprovedAnalysisHtml() {
          await downloadDocument(
            "media_file_video_analysis_export_approved_html",
            "VA_approved_analysis.html",
            "comparison.export.approved-analysis.error"
          );
        },
        /** The approved observations in the same CSV format as the OCR side. */
        async downloadApprovedAnalysisCsv() {
          await downloadDocument(
            "media_file_video_analysis_export_approved_csv",
            "VA_approved_results.csv",
            "comparison.export.approved-analysis.error"
          );
        },
        setLanguage(newLanguage: Language) {
            self.language = newLanguage;
        },
        setAnalysisConfidenceOption(confidenceOption: ConfidenceOptions) {
            self.analysisConfidenceOption = confidenceOption;
        },

        showAnalysisIndivididualConfidenceModal() {
            self.shouldShowAnalysisIndivididualConfidenceModal = true;
        },

        hideAnalysisIndivididualConfidenceModal() {
            self.shouldShowAnalysisIndivididualConfidenceModal = false;
        },
        setAnalysisOverallConfidence(overallConfidence: number) {
            self.analysisOverallConfidence = overallConfidence;
        },
    
        setIndividualAnalysisConfidence(confidenceLevels: ConfidenceLevels) {
            self.analysisIndividualConfidenceLevels = confidenceLevels;
            self.firstTimeChoosingAnalysisIndividualConfidence = false;
        },
        loadMainCodesDetected() {
            const videoAnalysisDiagram: any = window.document.getElementById(
              "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_main_codes_dic) {
              const mainCodeDetections = videoAnalysisDiagram.contentWindow.get_main_codes_dic();
              const mainCodes: CodeDetection[] = [];
    
              for (let key in mainCodeDetections) {
                mainCodes.push({ code: key, colorCode: mainCodeDetections[key], isVisible: true });
              }
    
              self.detectedMainCodes = cast(mainCodes);
            }
        },
        loadCharacterizationsDetected() {
            const videoAnalysisDiagram: any = window.document.getElementById(
              "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_characterizations_dic) {
              const characterizationDetections =
                videoAnalysisDiagram.contentWindow.get_characterizations_dic();
              const characterizations: CodeDetection[] = [];
    
              for (let key in characterizationDetections) {
                characterizations.push({ code: key, colorCode: characterizationDetections[key] });
              }
    
              self.detectedCharacterizations = cast(characterizations);
            }
        },
    
        changeView(isOverview: boolean) {
            self.isOverviewDisplayed = isOverview;
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.change_preview) {
                videoAnalysisDiagram.contentWindow.change_preview(isOverview);
            }
        },
    
        toggleMainCode(mainCode: string, isVisible: boolean) {
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (
                videoAnalysisDiagram &&
                videoAnalysisDiagram.contentWindow.toggle_main_code_visibility
            ) {
                videoAnalysisDiagram.contentWindow.toggle_main_code_visibility(mainCode, isVisible);
            }
        },
    
        addDamageToGraph(code: string, timestamp: number) {
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.add_damage_to_graph) {
                videoAnalysisDiagram.contentWindow.add_damage_to_graph(code, timestamp);
            }
        },
    
        updateDamageSeriesIntoGraph(code: string, timestamps: number[]) {
            const videoAnalysisDiagram: any = window.document.getElementById(
            "damages-video-analysis-iframe"
            );
            if (
            videoAnalysisDiagram &&
            videoAnalysisDiagram.contentWindow.update_damage_series_into_graph
            ) {
            videoAnalysisDiagram.contentWindow.update_damage_series_into_graph(code, timestamps);
            }
        },
        getPredictionsByCode(mainCode: string) {
            var confidence = 0;
            if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
              confidence = self.vaData.recommendedConfidenceLevels[mainCode]
            } else {
              if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
                if (self.analysisIndividualConfidenceLevels?.[mainCode]) {
                  confidence = self.analysisIndividualConfidenceLevels?.[mainCode];
                }
              } else {
                confidence = self.analysisOverallConfidence
              }
            }
            if (confidence > 1) confidence = confidence / 100;
            
            var object = self.vaData.predictionsPerAccuracy[mainCode + "_" + confidence];
            const predictions = Object.values(object) as TimestampPrediction[];
    
            return self.vaData.detectedTimestamps.map((x) => {
              const timestampPrediction = predictions.find((p) => p.timestamp === x);
    
              if (timestampPrediction) return timestampPrediction.predictionDetails.accuracy;
              return 0;
            });
        },
        changeMainCodeVisibility(mainCode: string) {
            let mainCodeDetections = toJS(self.detectedMainCodes);
            const index = mainCodeDetections.findIndex((det) => det.code === mainCode);
            mainCodeDetections[index] = {
              ...mainCodeDetections[index],
              isVisible: self.isMainCodeDetected(mainCode) ? !mainCodeDetections[index].isVisible : false,
            };
            self.detectedMainCodes = cast(mainCodeDetections);
    
            self.plotNeedsRelayout = true;
        },
        plotRelayoutDone() {
            self.plotNeedsRelayout = false;
        },
        buildPredictionGroupsForDetectedDamages(predictionGroups: PredictionGroup[]) {
            for (const prediction of self.allPredictions) {
              const codePrediction = prediction.predictionDetails;
              const hasMatchingMainCode = (x: PredictionGroup) =>
                x.mainCode === codePrediction.mainCode;
    
              const hasC1Code = (x: PredictionGroup) => x.hasC1Code && codePrediction.c1Code;
    
              const hasC2Code = (x: PredictionGroup) => x.hasC2Code && codePrediction.c2Code;
    
              const hasOnlyMainCode = (x: PredictionGroup) =>
                !x.hasC1Code && !codePrediction.c1Code && !x.hasC2Code && !codePrediction.c2Code;
    
              const predictionGroupC1 = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasC1Code(x)
              );
              const predictionGroupC2 = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasC2Code(x)
              );
              const predictionGroupMainCode = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasOnlyMainCode(x)
              );
    
              if (codePrediction.c1Code) {
                predictionGroupC1?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: codePrediction.c1Code,
                  timestamp: prediction.timestamp,
                });
              }
    
              if (codePrediction.c2Code) {
                predictionGroupC2?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: codePrediction.c2Code,
                  timestamp: prediction.timestamp,
                });
              }
    
              if (!codePrediction.c1Code && !codePrediction.c2Code) {
                predictionGroupMainCode?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: "",
                  timestamp: prediction.timestamp,
                });
              }
            }
        },
        hasMainCodeOnly(predictionGroup: PredictionGroup) {
            return !predictionGroup.hasC1Code && !predictionGroup.hasC2Code;
        },
        characterizationAtTime(time: number): DetectionsAtTime {
            const detectedCharacterizations: any = {};

            // O(1) bucket lookup instead of re-filtering all predictions on every call.
            const predictionsAtTime = (self as any).predictionsByRoundedTime.get(
              Math.round(time)
            ) as TimestampPrediction[] | undefined;

            if (!predictionsAtTime) return {};
    
            let combinedPredictionsAtTime: PredictionDetails[] = [];
    
            for (const mainCode of predictionsAtTime) {
              if (mainCode.predictionDetails)
                combinedPredictionsAtTime = [
                  ...combinedPredictionsAtTime,
                  ...[mainCode.predictionDetails],
                ];
            }
    
            if (!combinedPredictionsAtTime?.length) return {};
    
            for (const prediction of combinedPredictionsAtTime) {
              detectedCharacterizations[prediction.mainCode] ??= {};
              if (prediction.c1Code)
                detectedCharacterizations[prediction.mainCode][
                  SecondaryCodeEnum[SecondaryCodeEnum.C1]
                ] = prediction.c1Code;
              if (prediction.c2Code)
                detectedCharacterizations[prediction.mainCode][
                  SecondaryCodeEnum[SecondaryCodeEnum.C2]
                ] = prediction.c2Code;
            }
            return detectedCharacterizations;
        },
        getSecondaryCodeName(predictionGroup: PredictionGroup) {
            if (this.hasMainCodeOnly(predictionGroup)) {
              return "MC";
            }
    
            if (predictionGroup.hasC1Code) {
              return SecondaryCodeEnum[SecondaryCodeEnum.C1];
            }
    
            return SecondaryCodeEnum[SecondaryCodeEnum.C2];
        },
    }
})

export type AnalysisService = Instance<typeof AnalysisServiceModel>;